import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PRIVACY_POLICY_TEXT from '@/constants/privacyPolicy';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal bloquant affiché à un utilisateur qui n'a pas encore accepté la politique.
 * Doit être placé dans MainLayout et rester ouvert tant que non accepté.
 */
const AcceptTermsGate = ({ open, onAccepted }) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readEnd, setReadEnd] = useState(false);

  const accept = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/auth/accept-terms`, { method: 'POST', credentials: 'include' });
      if (r.ok) { toast.success('Politique acceptée'); onAccepted(); }
      else toast.error('Erreur, réessayez');
    } catch { toast.error('Erreur réseau'); }
    setSaving(false);
  };

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setReadEnd(true);
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck size={22} weight="duotone" className="text-[#F59E0B]" />
            Politique de confidentialité — Gazelle Security
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Pour continuer à utiliser la plateforme, vous devez lire et accepter les conditions ci-dessous.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto border border-[#1E293B] rounded-md p-4 bg-[#1E293B]/30 text-sm text-[#F8FAFC] whitespace-pre-line leading-relaxed"
          onScroll={onScroll}
          data-testid="terms-scroll-area"
        >
          {PRIVACY_POLICY_TEXT.replace(/\*\*(.+?)\*\*/g, '$1').replace(/^#+ /gm, '')}
        </div>

        <label className="flex items-start gap-2 p-3 rounded-md bg-[#1E293B]/50 border border-[#1E293B] cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={!readEnd}
            className="h-4 w-4 mt-0.5 accent-[#2563EB]"
            data-testid="accept-terms-checkbox"
          />
          <span className="text-sm text-[#F8FAFC]">
            J&apos;ai lu et j&apos;accepte la politique de confidentialité et les conditions d&apos;utilisation des données de <strong>Gazelle Security SARLU</strong>.
            {!readEnd && <span className="block text-xs text-[#F59E0B] mt-1 flex items-center gap-1"><Warning size={12} /> Défilez jusqu&apos;en bas du texte avant de cocher.</span>}
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            onClick={accept}
            disabled={!checked || saving}
            className="bg-[#10B981] hover:bg-[#059669] text-white"
            data-testid="accept-terms-btn"
          >
            {saving ? 'Enregistrement...' : "J'accepte et je continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptTermsGate;
